import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { relaunch } from '@tauri-apps/plugin-process';
import { check } from '@tauri-apps/plugin-updater';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DesktopUpdateProgressModal } from 'components/organisms/desktop-update-progress-modal';
import { useTranslate } from 'hooks/use-translation';
import { logger } from 'services';
import { isTauriApp } from 'utils/is-tauri';
const INITIAL_PROGRESS = {
    open: false,
    phase: 'downloading',
    percent: 0
};
const DesktopUpdaterContext = /*#__PURE__*/ createContext({
    availableVersion: null,
    isInstalling: false,
    installUpdate: ()=>undefined
});
export const useDesktopUpdater = ()=>useContext(DesktopUpdaterContext);
/**
 * Checks the channel updater endpoint for a newer desktop build.
 * Exposes availability via context (sidebar banner); progress in a modal after confirm.
 * No-ops outside Tauri / in `yarn tauri:dev`.
 */ export const DesktopUpdater = ({ children })=>{
    const translate = useTranslate();
    const promptedRef = useRef(false);
    const installingRef = useRef(false);
    const updateRef = useRef(null);
    const [availableVersion, setAvailableVersion] = useState(null);
    const [isInstalling, setIsInstalling] = useState(false);
    const [progress, setProgress] = useState(INITIAL_PROGRESS);
    const dismissError = useCallback(()=>{
        setProgress(INITIAL_PROGRESS);
        installingRef.current = false;
        setIsInstalling(false);
    }, []);
    const installUpdate = useCallback(()=>{
        const update = updateRef.current;
        if (!update || installingRef.current) {
            return;
        }
        installingRef.current = true;
        setIsInstalling(true);
        void (async ()=>{
            let downloaded = 0;
            let contentLength = 0;
            setProgress({
                open: true,
                phase: 'downloading',
                percent: 0,
                version: update.version
            });
            try {
                await update.downloadAndInstall((event)=>{
                    if (event.event === 'Started') {
                        var _event_data_contentLength;
                        contentLength = (_event_data_contentLength = event.data.contentLength) !== null && _event_data_contentLength !== void 0 ? _event_data_contentLength : 0;
                        downloaded = 0;
                        setProgress((prev)=>({
                                ...prev,
                                open: true,
                                phase: 'downloading',
                                percent: 0
                            }));
                        return;
                    }
                    if (event.event === 'Progress') {
                        downloaded += event.data.chunkLength;
                        const percent = contentLength > 0 ? Math.min(99, Math.round(downloaded / contentLength * 100)) : 0;
                        setProgress((prev)=>({
                                ...prev,
                                open: true,
                                phase: 'downloading',
                                percent
                            }));
                        return;
                    }
                    if (event.event === 'Finished') {
                        setProgress((prev)=>({
                                ...prev,
                                open: true,
                                phase: 'installing',
                                percent: 100
                            }));
                    }
                });
                setProgress((prev)=>({
                        ...prev,
                        open: true,
                        phase: 'relaunching',
                        percent: 100
                    }));
                await relaunch();
            } catch (err) {
                installingRef.current = false;
                setIsInstalling(false);
                logger.error('desktop update failed', {
                    error: err
                });
                setProgress({
                    open: true,
                    phase: 'error',
                    percent: 0,
                    version: update.version,
                    errorMessage: translate('desktop.updater.failed.body')
                });
            }
        })();
    }, [
        translate
    ]);
    useEffect(()=>{
        // Skip in `yarn tauri:dev` — stage updater check can relaunch over HMR.
        if (!isTauriApp() || process.env.TAURI_DEV === '1' || promptedRef.current) {
            return undefined;
        }
        let cancelled = false;
        const run = async ()=>{
            try {
                const update = await check();
                if (cancelled || !update || promptedRef.current) {
                    return;
                }
                promptedRef.current = true;
                updateRef.current = update;
                setAvailableVersion(update.version);
            } catch (err) {
                // Endpoint missing / offline is fine for early stage.
                logger.warn('desktop updater check skipped', {
                    error: err
                });
            }
        };
        void run();
        return ()=>{
            cancelled = true;
        };
    }, []);
    const value = useMemo(()=>({
            availableVersion,
            isInstalling,
            installUpdate
        }), [
        availableVersion,
        isInstalling,
        installUpdate
    ]);
    return /*#__PURE__*/ _jsxs(DesktopUpdaterContext.Provider, {
        value: value,
        children: [
            children,
            /*#__PURE__*/ _jsx(DesktopUpdateProgressModal, {
                open: progress.open,
                phase: progress.phase,
                percent: progress.percent,
                version: progress.version,
                errorMessage: progress.errorMessage,
                onDismissError: dismissError
            })
        ]
    });
};
